import { subHours } from 'date-fns';
import {
  FilterComparator,
  isComparisonFilterExpression,
  type GroupingFilterExpression,
} from '@@/packages/shared/libs/search/types';

export type ClientDocumentFilterParams = {
  expiryStatus?: string[];
  addedWithin?: string;
  addedAfter?: string;
  addedBefore?: string;
};

// The endpoint takes a bare date for its absolute bounds, matching the `date`
// format it publishes.
const asDate = (value: Date) => value.toISOString().slice(0, 10);

const shiftDays = (value: Date, days: number) => {
  const shifted = new Date(value);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted;
};

// A relative filter's value is a Go duration ('720h'), which is also what the
// backend's addedWithin takes — so the window stays relative all the way to the
// database rather than freezing on the day the filter was built.
const RELATIVE_DURATION = /^\d+h$/;

function dateAddedParams(comparator: FilterComparator, value: string): ClientDocumentFilterParams {
  if (RELATIVE_DURATION.test(value)) {
    const hours = Number(value.slice(0, -1));
    const instant = subHours(new Date(), hours);

    switch (comparator) {
      // 'added after N ago' is the window the presets are really for.
      case FilterComparator.RelativePastGreaterThan:
        return { addedWithin: value };
      // The remaining two are about a moment in the past rather than a window,
      // so they resolve to a date here; there is no relative param for them.
      case FilterComparator.RelativePastLessThan:
        return { addedBefore: asDate(shiftDays(instant, -1)) };
      case FilterComparator.RelativePastEqualDate:
        return { addedAfter: asDate(instant), addedBefore: asDate(instant) };
      default:
        return {};
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return {};

  switch (comparator) {
    case FilterComparator.EqualDate:
      return { addedAfter: asDate(date), addedBefore: asDate(date) };
    // The bounds are inclusive, so a strict comparison steps a day clear of the
    // date the user named.
    case FilterComparator.GreaterThan:
      return { addedAfter: asDate(shiftDays(date, 1)) };
    case FilterComparator.LessThan:
      return { addedBefore: asDate(shiftDays(date, -1)) };
    default:
      return {};
  }
}

/**
 * Translates the filter expression `Filters.vue` emits into the endpoint's named
 * query params.
 *
 * Two deliberate simplifications, both from the expiry buckets partitioning the
 * documents — every document sits in exactly one:
 *
 * - 'includes all' is treated as 'includes any'. For a single bucket the two are
 *   identical; for several, 'all of' could only ever match nothing, and showing
 *   the chosen buckets is more use than an empty table.
 * - 'excludes any'/'excludes all' become the complement of the chosen buckets,
 *   which the closed set makes exact.
 *
 * `ne_date` has no equivalent param — 'not on the 5th' is an OR across two
 * bounds, which named params cannot express — so an operand using it is dropped.
 */
export function toFilterParams(
  filter: GroupingFilterExpression | undefined,
): ClientDocumentFilterParams {
  const params: ClientDocumentFilterParams = {};
  if (!filter) return params;

  for (const operand of filter.operands) {
    if (!isComparisonFilterExpression(operand)) continue;

    if (operand.field === 'expiryStatus') {
      params.expiryStatus = (Array.isArray(operand.value) ? operand.value : [operand.value]).map(
        String,
      );
      continue;
    }

    if (operand.field === 'createdAt') {
      Object.assign(params, dateAddedParams(operand.comparator, String(operand.value)));
    }
  }

  return params;
}
